import { useState } from 'react';
import { Button, Card, Collapse, Group, Image, SimpleGrid, Stack, Text } from '@mantine/core';
import { IconPhotoCheck } from '@tabler/icons-react';
import {
  FACTORY_ASSET_PLATFORM_COPY,
  FACTORY_DELIVERABLE_SLOTS,
  type FactoryAssetPlatform,
} from '@/lib/constants/appFactory';
import { useFactoryAssetSets } from '../useAppFactory';
import { BrandKitUtils } from '../BrandKitUtils';
import { TvDevicePreviews } from './TvDevicePreviews';

interface AppCurrentImagesProps {
  appCfgId: number;
  platform: FactoryAssetPlatform;
}

/**
 * The LIVE deliverable set of one platform (the serving location builds and
 * devices load from) — console-era apps included, which is why this reads the
 * factory's currentImages instead of any dashboard-side upload state.
 * Platform-generic since 3.34.0: Fire TV's set also carries the store-listing
 * art the holder downloads from here for the developer console. Degrades to
 * nothing when the factory gateway is off (matching the Brand Kit card).
 */
export function AppCurrentImages({ appCfgId, platform }: AppCurrentImagesProps) {
  const assetSetsQuery = useFactoryAssetSets(appCfgId, platform);
  const [onTv, setOnTv] = useState(false);
  if (assetSetsQuery.isError) return null;

  const current = assetSetsQuery.data?.currentImages ?? [];
  if (current.length === 0) return null;

  const labelByName = new Map(FACTORY_DELIVERABLE_SLOTS[platform].map((s) => [s.name, s.label]));
  const copy = FACTORY_ASSET_PLATFORM_COPY[platform];

  return (
    <Card withBorder radius="md" p="md">
      <Stack gap="md">
        <Group gap="xs">
          <IconPhotoCheck size={20} />
          <Text fw={600} size="md">
            Current Images
          </Text>
        </Group>
        <Text size="sm" c="dimmed">
          {copy.currentIntro}
        </Text>
        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
          {current.map((img) => (
            <Stack key={img.name} gap={4}>
              <Image src={img.url} alt={img.name} radius="sm" h={80} fit="contain" bg="gray.1" />
              <Text size="xs" c="dimmed" ta="center" truncate>
                {labelByName.get(img.name) ?? img.name}
              </Text>
            </Stack>
          ))}
        </SimpleGrid>
        {platform === 'firetv' && (
          <Stack gap="xs">
            <Button variant="subtle" size="compact-sm" w="fit-content" onClick={() => setOnTv((v) => !v)}>
              {onTv ? 'Hide the TV screens' : 'See it on a TV'}
            </Button>
            <Collapse in={onTv}>
              {onTv && <TvDevicePreviews files={BrandKitUtils.previewFileMap(null, current) ?? {}} />}
            </Collapse>
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
